import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Bot, Copy, RefreshCw, Loader2, ShieldAlert, ShieldX, PlugZap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MCP_URL = `${process.env.REACT_APP_BACKEND_URL}/api/mcp/code-access`;

/**
 * Super-admin only card for the "Claude Code Access (MCP)" integration.
 * Auth is now full OAuth 2.1 (claude.ai's custom connector flow does its
 * own registration/consent/token exchange) — this card just shows the MCP
 * server URL, lets the admin enable/disable the whole integration, and
 * lists/revokes the OAuth clients that have connected.
 */
export const McpCodeAccessSettings = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/admin/integrations/mcp-code-access`);
      setStatus(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load MCP status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleToggle = async () => {
    if (!status) return;
    const next = !status.enabled;
    setToggling(true);
    try {
      await api.put(`${API}/admin/integrations/mcp-code-access`, { enabled: next });
      toast.success(next ? 'MCP code access enabled' : 'MCP code access disabled');
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setToggling(false);
    }
  };

  const handleRevoke = async (clientId, clientName) => {
    if (!window.confirm(`Revoke access for "${clientName || clientId}"? It will need to reconnect and get approved again.`)) return;
    setRevokingId(clientId);
    try {
      await api.post(`${API}/admin/integrations/mcp-code-access/oauth-clients/${clientId}/revoke`);
      toast.success('Client revoked');
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke client');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="rounded-xl border bg-white p-6 flex items-center justify-center" data-testid="mcp-settings-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const clients = status?.oauth_clients || [];

  return (
    <div className="rounded-xl border bg-white p-4" data-testid="mcp-code-access-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-bold text-gray-900">Claude Code Access (MCP)</h3>
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
              enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
            }`}
            data-testid="mcp-status-indicator"
          >
            {enabled ? 'Active' : 'Disabled'}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            onClick={fetchStatus} disabled={loading}
            data-testid="mcp-settings-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handleToggle} disabled={toggling}
            className={enabled ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}
            data-testid="mcp-disable-toggle-btn"
          >
            {toggling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            {enabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        Gives Claude read-only access to this codebase (source files only — no secrets, env files, or data)
        via a remote OAuth-authenticated MCP connector. Anyone you approve in the consent screen gets access.
      </p>

      {!enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 flex items-start gap-2 text-sm text-amber-800" data-testid="mcp-disabled-banner">
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Disabled — all MCP and OAuth requests currently return 401/403.
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-gray-700">MCP Server URL</label>
          <div className="flex gap-2 mt-1">
            <input
              readOnly
              value={MCP_URL}
              className="flex-1 font-mono text-sm rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700"
              data-testid="mcp-server-url-input"
            />
            <Button
              variant="outline" size="sm"
              onClick={() => copyToClipboard(MCP_URL, 'MCP Server URL')}
              data-testid="mcp-copy-url-btn"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Paste this as the custom connector URL in Claude — it will handle OAuth sign-in automatically.
          </p>
        </div>

        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <PlugZap className="w-4 h-4 text-gray-500" />
            <label className="text-xs font-semibold text-gray-700">Connected OAuth clients</label>
          </div>
          {clients.length === 0 ? (
            <p className="text-xs text-gray-400" data-testid="mcp-oauth-clients-empty">
              No clients have connected yet.
            </p>
          ) : (
            <div className="space-y-2" data-testid="mcp-oauth-clients-list">
              {clients.map((c) => (
                <div
                  key={c.client_id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                  data-testid={`mcp-oauth-client-row-${c.client_id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.client_name || 'Unnamed client'}</p>
                    <p className="text-[11px] text-gray-400">
                      Connected {c.created_at ? new Date(c.created_at).toLocaleString('en-GB') : '—'}
                      {' · '}
                      Last used {c.last_used_at ? new Date(c.last_used_at).toLocaleString('en-GB') : 'Never'}
                    </p>
                  </div>
                  <Button
                    variant="outline" size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleRevoke(c.client_id, c.client_name)}
                    disabled={revokingId === c.client_id}
                    data-testid={`mcp-revoke-client-btn-${c.client_id}`}
                  >
                    {revokingId === c.client_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldX className="w-3.5 h-3.5 mr-1" />}
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-500" data-testid="mcp-last-accessed">
            Last accessed:{' '}
            {status?.last_accessed_at
              ? new Date(status.last_accessed_at).toLocaleString('en-GB')
              : 'Never'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default McpCodeAccessSettings;
